import { useQueryParameter } from '@/context/QueryParameter';
import { InvoiceReportOrderByField } from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import type {
  AdminInvoiceReportsSearchQuery,
  FetchAdminInvoiceReportsArgs,
  SortField,
} from '@/features/admin_invoice_reports/types';
import {
  isSortFieldKey,
  mapApiSortFieldToColumnKey,
  mapColumnKeyToApiSortField,
} from '@/features/admin_invoice_reports/types';
import { buildOrderByArray } from '@/features/admin_invoice_reports/utils/buildOrderByArray';
import { useUpdateURLSearchParams } from '@/hooks/useConvertToURLSearchParams';
import { isEqual } from '@/utils/isEqual';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { MultiColumnSortState } from '../Table/types';

const EMPTY_SORT_ORDERS: MultiColumnSortState[] = [];

const VALID_ORDER_BY_FIELDS: readonly string[] = Object.values(
  InvoiceReportOrderByField
);

const isValidOrderByField = (
  value: string
): value is InvoiceReportOrderByField => VALID_ORDER_BY_FIELDS.includes(value);

/** Parse `sort_fields` / `sort_orders` URL params (comma-separated, same as invoice_reports). */
const parseSortOrdersFromParams = (
  sortFieldsParam: string | null,
  sortOrdersParam: string | null
): MultiColumnSortState[] => {
  if (!sortFieldsParam || !sortOrdersParam) return EMPTY_SORT_ORDERS;

  const fields = sortFieldsParam.split(',');
  const orders = sortOrdersParam.split(',');
  if (fields.length !== orders.length) return EMPTY_SORT_ORDERS;

  const result: MultiColumnSortState[] = [];
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const order = orders[i];
    if (!field || !order) continue;
    if (!isValidOrderByField(field)) continue;
    const columnKey = mapApiSortFieldToColumnKey(field);
    if (!columnKey) continue;
    let orderValue: 'ascend' | 'descend';
    if (order === 'asc') {
      orderValue = 'ascend';
    } else {
      orderValue = 'descend';
    }
    result.push({ order: orderValue, columnKey });
  }

  return result.length > 0 ? result : EMPTY_SORT_ORDERS;
};

type UseHandleSearchQueryResult = {
  fetchArgs: FetchAdminInvoiceReportsArgs;
  searchQuery: AdminInvoiceReportsSearchQuery;
  sortOrders: MultiColumnSortState[];
  onSearch: (
    query: AdminInvoiceReportsSearchQuery,
    refetchTable?: () => void
  ) => void;
  onMultiColumnSort: (sortStates: MultiColumnSortState[]) => void;
};

export const useAdminInvoiceReportsHandleSearchQuery = (
  initialQuery: AdminInvoiceReportsSearchQuery = {}
): UseHandleSearchQueryResult => {
  const [param] = useQueryParameter();
  const [searchQuery, setSearchQuery] =
    useState<AdminInvoiceReportsSearchQuery>(initialQuery);

  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const sortFieldsParam = param.get('sort_fields');
  const sortOrdersParam = param.get('sort_orders');

  const sortOrders = useMemo(
    () => parseSortOrdersFromParams(sortFieldsParam, sortOrdersParam),
    [sortFieldsParam, sortOrdersParam]
  );

  const updateSearchParams = useUpdateURLSearchParams();

  const onSearch = useCallback(
    (query: AdminInvoiceReportsSearchQuery, refetchTable?: () => void) => {
      const currentSearchQuery = searchQueryRef.current;

      // Page-only: from pagination navigation
      if ('page' in query && Object.keys(query).length === 1) {
        const merged = {
          ...currentSearchQuery,
          page: query.page,
          sort_fields: currentSearchQuery.sort_fields,
          sort_orders: currentSearchQuery.sort_orders,
        };
        setSearchQuery(merged);
        updateSearchParams(
          Object.fromEntries(
            Object.entries(merged).filter(([, v]) => v !== undefined)
          )
        );
        return;
      }
      // Full search from form button: query has no page (stripped by Search component)
      // Compare criteria only — exclude page from current searchQuery
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { page: _cp, ...currentCriteria } = currentSearchQuery;
      if (isEqual(currentCriteria, query)) {
        refetchTable?.();
        return;
      }
      const newSearchQuery = {
        ...query,
        page: 1,
        sort_fields: currentSearchQuery.sort_fields,
        sort_orders: currentSearchQuery.sort_orders,
      };
      setSearchQuery(newSearchQuery);
      updateSearchParams(
        Object.fromEntries(
          Object.entries(newSearchQuery).filter(([, v]) => v !== undefined)
        )
      );
    },
    [updateSearchParams]
  );

  const onMultiColumnSort = useCallback(
    (sortStates: MultiColumnSortState[]) => {
      const currentSearchQuery = searchQueryRef.current;
      let newSortFields: string[] | undefined;
      let newSortOrders: string[] | undefined;

      if (sortStates.length > 0) {
        const validStates = sortStates.filter(
          (s): s is MultiColumnSortState & { columnKey: SortField } =>
            isSortFieldKey(s.columnKey)
        );
        if (validStates.length > 0) {
          newSortFields = [
            validStates
              .map((s) => mapColumnKeyToApiSortField(s.columnKey))
              .join(','),
          ];
          newSortOrders = [
            validStates
              .map((s) => (s.order === 'ascend' ? 'asc' : 'desc'))
              .join(','),
          ];
        } else {
          newSortFields = undefined;
          newSortOrders = undefined;
        }
      }

      const newSearchQuery: AdminInvoiceReportsSearchQuery = {
        ...currentSearchQuery,
        sort_fields: newSortFields,
        sort_orders: newSortOrders,
      };

      setSearchQuery(newSearchQuery);
      updateSearchParams(
        Object.fromEntries(
          Object.entries(newSearchQuery).filter(([, v]) => v !== undefined)
        )
      );
    },
    [updateSearchParams]
  );

  const orderBy = useMemo(() => buildOrderByArray(sortOrders), [sortOrders]);

  const fetchArgs: FetchAdminInvoiceReportsArgs = useMemo(() => {
    // Strip pagination-only fields before sending to the API query body
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, sort_fields, sort_orders, ...apiQuery } = searchQuery;
    return {
      page: page ?? 1,
      perPage: 50,
      query: apiQuery,
      order_by: orderBy,
    };
  }, [searchQuery, orderBy]);

  return { fetchArgs, searchQuery, sortOrders, onSearch, onMultiColumnSort };
};
